//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr } from '@shared/SchemaTypes'

import { PoolOffchainDataCTO } from '@shared/PoolOffchainDataCTO'

import { otAssert } from '@shared/conversions'
import { PoolBaseAccountCTO } from '@shared/sharedTypes'

import { logOT } from '@shared/loggingShared'
import { fetchPoolAccountCTO, poolOffchainDataFetch } from '@react/fetchers/PoolFetchers'

export const fetchAllOffchainData = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddrs: EthAddr[],
  poolOffchainDataCTOs: Record<EthAddr, PoolOffchainDataCTO>
): Promise<{
  fetchedPoolOffchainData: PoolOffchainDataCTO[]
  poolOffchainDataForPoolAddrs: Record<EthAddr, PoolOffchainDataCTO>
}> => {
  const poolOffchainDataForPoolAddrs: Record<EthAddr, PoolOffchainDataCTO> = {}
  const fetchedPoolOffchainData: PoolOffchainDataCTO[] = []

  otAssert(!!serverApiBaseUrl, 'serverApiBaseUrl')

  logOT.info('LenderExplorerPage FETCH OFFCHAIN DATA START')

  const promises = []
  for (let i = 0; i < poolAddrs.length; i++) {
    const poolAddr = poolAddrs[i]
    const poolOffchainDataCTO = poolOffchainDataCTOs[poolAddr]
    if (poolOffchainDataCTO) {
      poolOffchainDataForPoolAddrs[poolAddr] = poolOffchainDataCTO
    } else {
      const promise = new Promise((resolve, reject) => {
        poolOffchainDataFetch(serverApiBaseUrl, restAPIKey, poolAddr)
          .then((poolOffchainDataCTO) => {
            if (poolOffchainDataCTO) {
              fetchedPoolOffchainData.push(poolOffchainDataCTO)
            }
            resolve(poolOffchainDataCTO)
          })
          .catch((e) => {
            console.error('Error fetching offchain data', e)
            reject(e)
          })
      })
      promises.push(promise)
    }
  }
  const results = (await Promise.all(promises)) as PoolOffchainDataCTO[]
  for (let i = 0; i < results.length; i++) {
    const poolOffchainDataCTO = results[i]
    if (poolOffchainDataCTO) {
      poolOffchainDataForPoolAddrs[poolOffchainDataCTO.poolAddr] = poolOffchainDataCTO
    }
  }
  return { fetchedPoolOffchainData, poolOffchainDataForPoolAddrs }
}

export const fetchNeedPoolAccountCTOs = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  lenderAddrsToFetch: EthAddr[],
  poolAddrs: EthAddr[],
  poolAccountCTOs: Record<EthAddr, Record<EthAddr, PoolBaseAccountCTO>>
): Promise<{
  fetchedPoolAccountCTOs: PoolBaseAccountCTO[]
}> => {
  const fetchedPoolAccountCTOs: PoolBaseAccountCTO[] = []
  const lenderAddr = lenderAddrsToFetch[0]

  logOT.info('LenderExplorerPage DATA for Lender', lenderAddr)

  const promises = []
  for (let i = 0; i < lenderAddrsToFetch.length; i++) {
    const lenderAddr = lenderAddrsToFetch[i]
    for (let j = 0; j < poolAddrs.length; j++) {
      const poolAddr = poolAddrs[j]

      const poolAccountCTO: PoolBaseAccountCTO | undefined = poolAccountCTOs[poolAddr]
        ? poolAccountCTOs[poolAddr][lenderAddr]
        : undefined
      if (!poolAccountCTO) {
        promises.push(fetchPoolAccountCTO(serverApiBaseUrl, restAPIKey, poolAddr, lenderAddr))
      }
    }
  }

  const results = await Promise.all(promises)
  for (let j = 0; j < results.length; j++) {
    const poolAccountCTO = results[j]

    if (poolAccountCTO) {
      fetchedPoolAccountCTOs.push(poolAccountCTO)
    }
  }
  return { fetchedPoolAccountCTOs }
}
